import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Hook for presence tracking - sends heartbeat to server
 * Updates user's online status based on activity
 */
export function usePresenceTracking(isAuthenticated = false) {
  const location = useLocation();
  const lastActivityRef = useRef(Date.now());
  const heartbeatIntervalRef = useRef(null);
  const isActiveRef = useRef(true);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);

  const sendHeartbeatRef = useRef(() => {});

  // Send heartbeat to server
  const sendHeartbeat = useCallback(async () => {
    if (!isAuthenticated) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;

    // Okt 2026 (Fase 6): satu heartbeat sekali jalan + batas waktu, supaya di
    // sinyal lemah request tidak menggantung dan menumpuk tiap 45 detik.
    if (inFlightRef.current) { pendingRef.current = true; return; }
    inFlightRef.current = true;
    try {
      await axios.post(
        `${API}/presence/heartbeat`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { current_route: location.pathname },
          timeout: 15000
        }
      );
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('Heartbeat failed:', error.message);
    } finally {
      inFlightRef.current = false;
      // Ada permintaan (mis. pindah halaman) selagi heartbeat jalan: kirim satu kali lagi.
      if (pendingRef.current) { pendingRef.current = false; sendHeartbeatRef.current(); }
    }
  }, [isAuthenticated, location.pathname]);
  sendHeartbeatRef.current = sendHeartbeat;

  // Track user activity
  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    isActiveRef.current = true;
  }, []);

  // Setup heartbeat interval and activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    // Only send heartbeat if user was active in the last 5 minutes
    const scheduleHeartbeat = () => {
      heartbeatIntervalRef.current = setInterval(() => {
        const idleTime = Date.now() - lastActivityRef.current;
        const fiveMinutes = 5 * 60 * 1000;

        if (idleTime < fiveMinutes && isActiveRef.current) {
          sendHeartbeat();
        }
      }, 45000); // 45 seconds
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Setup heartbeat interval (every 45 seconds)
    scheduleHeartbeat();

    // Activity event listeners
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

    // Throttle activity updates to avoid too many calls
    let activityTimeout = null;
    const throttledActivity = () => {
      if (!activityTimeout) {
        handleActivity();
        activityTimeout = setTimeout(() => {
          activityTimeout = null;
        }, 10000); // Throttle to once per 10 seconds
      }
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, throttledActivity, { passive: true });
    });

    // Perf fix (Sep 2026) — Visibility change handler: hard-pause the
    // interval (not just a soft flag) while the tab is hidden, and resume
    // with one immediate heartbeat + a fresh 45s interval when visible again.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isActiveRef.current = false;
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      } else {
        isActiveRef.current = true;
        sendHeartbeat(); // Send heartbeat when tab becomes visible
        if (!heartbeatIntervalRef.current) {
          scheduleHeartbeat();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, throttledActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, sendHeartbeat, handleActivity]);

  // Send heartbeat on route change
  useEffect(() => {
    if (isAuthenticated) {
      sendHeartbeat();
    }
  }, [location.pathname, isAuthenticated, sendHeartbeat]);

  return { sendHeartbeat };
}

export default usePresenceTracking;
